import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Ban, Check, Loader2, Undo2, X } from 'lucide-react';
import { useId } from 'react';

import {
  Confirmation,
  ConfirmationAccepted,
  ConfirmationAction,
  ConfirmationActions,
  ConfirmationRejected,
  ConfirmationRequest,
  ConfirmationTitle,
} from '@/components/ai-elements';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  mirandaRichApi,
  type ProposalChange,
  type ProposalPartial,
  type ProposalPartialPending,
  type ProposalResolution,
  type ProposalStatus,
  type WriteToolResult,
} from '@/services/miranda-rich-chat';

import { useDecision, type DecisionController } from './decision';
import { ReservationSheetLink } from './reservation-card';

const WRITE_KINDS = ['executed', 'proposal', 'refused'];

/** La sortida d'una tool segueix el contracte d'escriptura de la peça de polítiques? */
export function isWriteToolResult(output: unknown): output is WriteToolResult {
  return (
    !!output &&
    typeof output === 'object' &&
    WRITE_KINDS.includes((output as { kind?: unknown }).kind as string)
  );
}

export type ProposalAction = 'approve' | 'reject' | 'undo';

/** Les tres crides que la targeta fa. Es poden substituir als tests. */
export interface ProposalActions {
  approve: (id: string) => Promise<ProposalResolution>;
  reject: (id: string) => Promise<ProposalResolution>;
  undo: (id: string) => Promise<ProposalResolution>;
}

const DEFAULT_ACTIONS: ProposalActions = {
  approve: mirandaRichApi.approveProposal,
  reject: mirandaRichApi.rejectProposal,
  undo: mirandaRichApi.undoProposal,
};

/**
 * «Aprova», «Rebutja» o «Desfés» d'una proposta. Fora del component perquè es pugui provar sense
 * DOM: dos clics seguits passen dues vegades per aquí i només el primer envia res.
 *
 * `id` és el `proposalId` (aprovar, rebutjar) o l'`undoId` (desfer). `proposalId` és la clau de
 * la consulta d'estat de la targeta (el `proposalId` o l'`undoId`), on es desa la resposta.
 */
export function decideProposal({
  controller,
  actions,
  queryClient,
  action,
  id,
  proposalId,
}: {
  controller: DecisionController<ProposalAction, ProposalResolution>;
  actions: ProposalActions;
  queryClient: QueryClient;
  action: ProposalAction;
  id: string | undefined;
  proposalId?: string;
}): boolean {
  if (!id) return false;
  return controller.run(action, async () => {
    const resolution = await actions[action](id);
    // Ha escrit: aplicada, desfeta, o fallida a mitges (`partial`, també només amb reserves
    // desades al PMS que Beds24 no ha confirmat, `beds24Pending`).
    let wrote = wroteSomething(resolution);
    if (proposalId) {
      // Un «Desfés» que falla no diu com ha quedat la fila: si no s'ha escrit res, segueix feta
      // (`approved`); si n'ha escrit una part (`wrote`), el servidor la dona per `failed` («Amb
      // error»). No es desa com a estat: es torna a demanar, i el clic no acaba fins que arriba la
      // resposta, perquè la targeta pinti de seguida el que diu el servidor (com en reobrir).
      if (action === 'undo' && resolution.status === 'failed') {
        try {
          await queryClient.invalidateQueries({ queryKey: ['agent-proposals', proposalId] });
        } catch {
          // Sense resposta, la targeta es queda amb el que ja sabia; l'error del clic es pinta igual.
        }
        wrote = queryClient.getQueryData<ProposalResolution>(['agent-proposals', proposalId])?.status === 'failed';
      } else {
        queryClient.setQueryData(['agent-proposals', proposalId], resolution);
      }
    }
    if (wrote) invalidateAfterWrite(queryClient);
    return resolution;
  });
}

/**
 * El que mostra una reserva i pot haver canviat després d'una escriptura feta des d'una targeta:
 * el calendari, els llistats i la fitxa oberta al costat del giny (`reservation-by-beds24`). Les
 * mateixes claus que el xat refresca quan un torn executa una escriptura (`rich-chat.tsx`).
 */
export const QUERIES_AFTER_CARD_WRITE = [['calendar-bookings'], ['reservations'], ['reservation-by-beds24']] as const;

export function invalidateAfterWrite(queryClient: QueryClient) {
  for (const queryKey of QUERIES_AFTER_CARD_WRITE) void queryClient.invalidateQueries({ queryKey });
}

const STATUS_TEXT: Record<ProposalStatus, string> = {
  pending: 'Espera la teva decisió',
  approved: 'Aprovada i aplicada',
  rejected: 'Rebutjada: no s\'ha canviat res',
  undone: 'Desfeta: torna a ser com abans',
  failed: 'No s\'ha pogut aplicar',
};

/** Com està una acció ja executada, vista des de la targeta. */
export type ExecutedState = 'done' | 'undone' | 'in-progress' | 'failed';

const EXECUTED_BADGE: Record<ExecutedState, string> = {
  done: 'Fet',
  undone: 'Desfeta',
  'in-progress': 'En curs',
  failed: 'Amb error',
};

/** Cada quant es torna a demanar l'estat d'una targeta que el servidor encara aplica o desfà. */
export const APPLYING_POLL_MS = 3_000;

/**
 * La resposta que mana a la targeta: la del clic d'ara, si és definitiva; si no (`pending`: el
 * servidor encara aplicava quan ha respost, o un «Desfés» `failed`, que es torna a demanar), la
 * consulta d'estat, que es refresca sola (`statusPollInterval`).
 */
export function currentResolution(
  resolved: ProposalResolution | undefined,
  fetched: ProposalResolution | undefined,
): ProposalResolution | undefined {
  if (resolved && resolved.status !== 'pending') return resolved;
  return fetched ?? resolved;
}

/**
 * L'estat d'una acció executada: mana la resposta del «Desfés» d'ara i, si no n'hi ha, el que
 * diu `GET /agent/proposals/:undoId`. Només `done` ofereix «Desfés».
 *
 * - `approved` (la fila és `executed`) o cap resposta: feta, es pot desfer.
 * - `undone`: desfeta.
 * - `pending`: el servidor l'està aplicant o desfent. **No és una proposta per aprovar:** una
 *   acció executada no en té, de botons d'aprovar; es pinta «en curs», sense cap botó, i la
 *   targeta torna a demanar l'estat fins que acaba.
 * - `failed`, o qualsevol altre estat: l'error, i cap botó (el servidor ja no la deixa desfer).
 *
 * Un «Desfés» d'ara que respon `pending` o `failed` no és la darrera paraula: mana el que torna
 * la consulta d'estat, que `decideProposal` ja ha tornat a demanar. Un `failed` que no ha escrit
 * res torna `approved` (feta, amb l'error del clic); un que n'ha escrit una part torna `failed`
 * («Amb error»), com en reobrir la conversa.
 */
export function executedView(
  resolved: ProposalResolution | undefined,
  fetched: ProposalResolution | undefined,
): { state: ExecutedState; error?: string } {
  if (resolved?.status === 'undone') return { state: 'undone' };
  if (resolved && resolved.status !== 'pending' && resolved.status !== 'failed') return { state: 'done' };
  if (resolved && !fetched) return { state: resolved.status === 'pending' ? 'in-progress' : 'done' };
  switch (fetched?.status) {
    case undefined:
    case 'approved':
      return { state: 'done' };
    case 'undone':
      return { state: 'undone' };
    case 'pending':
      return { state: 'in-progress' };
    default:
      return { state: 'failed', error: fetched?.error || 'No s\'ha pogut aplicar o desfer. Mira la reserva.' };
  }
}

/**
 * La fila d'`agent_proposals` de què es demana l'estat: la proposta (`proposalId`) o l'acció ja
 * executada (el seu `undoId` és l'`id` de la mateixa taula). Sense cap dels dos no hi ha res a
 * demanar.
 */
export function proposalStatusId(result: WriteToolResult): string | undefined {
  if (result.kind === 'proposal') return result.proposalId;
  if (result.kind === 'executed') return result.undoId;
  return undefined;
}

/**
 * La consulta d'estat d'una targeta. La fan servir la targeta i el missatge que la porta
 * (`rich-message.tsx`, per saber si el text és d'abans de la decisió): mateixa clau, una sola
 * petició. La resposta d'un clic també hi queda desada (`decideProposal`).
 */
export function proposalStatusQuery(statusId: string | undefined, enabled = true) {
  return {
    queryKey: ['agent-proposals', statusId] as const,
    queryFn: () => mirandaRichApi.getProposal(statusId as string),
    enabled: enabled && !!statusId,
    retry: false,
    staleTime: 30_000,
  };
}

/**
 * La targeta ja està resolta? `current` és l'estat que hi ha a la consulta d'estat (el que diu el
 * servidor o la resposta del clic, que s'hi desa).
 *
 * - Una proposta: qualsevol estat que no sigui `pending` (aprovada, rebutjada, desfeta, fallada o
 *   aplicada en part). Sense resposta, val el que deia la sortida de la tool.
 * - Una acció executada: només si ja no és «feta» (desfeta, o fallada). Mentre segueix feta o
 *   s'està aplicant, el que Miranda en va dir encara és cert.
 * - Una acció que Miranda no fa sola (`refused`): no hi ha res per decidir.
 */
export function isCardResolved(result: WriteToolResult, current: ProposalResolution | undefined): boolean {
  if (result.kind === 'proposal') {
    const status = current?.status ?? (isProposalStatus(result.status) ? result.status : 'pending');
    return status !== 'pending';
  }
  if (result.kind === 'executed') {
    const { state } = executedView(undefined, current);
    return state === 'undone' || state === 'failed';
  }
  return false;
}

/**
 * L'estat d'una proposta a la targeta i si s'està aplicant.
 *
 * Un clic que respon `pending` vol dir que algú (una altra targeta, pestanya o persona) ja l'ha
 * reclamada i el servidor encara l'aplicava quan ha deixat d'esperar: la targeta diu «S'està
 * aplicant…», sense botons (el seu `DecisionController` ja ha decidit i no en tornaria a enviar
 * cap), i torna a demanar l'estat fins que surt de `pending` (`statusPollInterval`).
 *
 * Sense clic, `pending` és el d'una proposta que espera decisió (la consulta d'estat no distingeix
 * «esperant» d'«aplicant»): botons vius; si un clic arriba mentre s'aplica, torna aquí.
 */
export function proposalView(
  resolved: ProposalResolution | undefined,
  fetched: ProposalResolution | undefined,
  outputStatus: unknown,
): { status: ProposalStatus; applying: boolean } {
  const current = currentResolution(resolved, fetched);
  const status = current?.status ?? (isProposalStatus(outputStatus) ? outputStatus : 'pending');
  return { status, applying: resolved?.status === 'pending' && status === 'pending' };
}

/**
 * Cada quant torna a demanar l'estat la targeta (`refetchInterval`), o `false` si no cal: només
 * mentre el servidor aplica o desfà. Una acció executada en `pending` sempre s'està desfent; una
 * proposta, només després d'un clic que ha respost `pending` (si no, espera decisió).
 */
export function statusPollInterval(
  result: WriteToolResult,
  resolved: ProposalResolution | undefined,
  current: ProposalResolution | undefined,
): number | false {
  if (current?.status !== 'pending') return false;
  if (result.kind === 'executed') return APPLYING_POLL_MS;
  return result.kind === 'proposal' && resolved?.status === 'pending' ? APPLYING_POLL_MS : false;
}

/**
 * El text de Miranda d'un missatge és «d'abans»? Ho és si alguna targeta d'escriptura del mateix
 * missatge ja està resolta: el text es va escriure abans de la decisió («encara no s'ha escrit
 * res») i no es pot reescriure; el xat el plega darrere d'un control (`BEFORE_DECISION_LABEL`) i
 * el que es veu és l'estat de la targeta.
 */
export function isTextFromBeforeDecision(
  cards: ReadonlyArray<{ result: WriteToolResult; current: ProposalResolution | undefined }>,
): boolean {
  return cards.some(({ result, current }) => isCardResolved(result, current));
}

/** El control que plega el text d'abans de la decisió («… · mostra» / «… · amaga»). */
export const BEFORE_DECISION_LABEL = 'Text d\'abans de la decisió';

/** El text d'una proposta fallida que s'havia aplicat en part: «no s'ha pogut aplicar» seria fals. */
export const PARTIAL_STATUS_TEXT = 'Aplicada en part: no s\'ha acabat';

/** Desades al PMS però sense la confirmació de Beds24, i res més: no ha fallat cap reserva. */
export const BEDS24_PENDING_STATUS_TEXT = 'Desada, falta Beds24';

/** La línia del grup de reserves que Beds24 no ha confirmat. */
export const BEDS24_PENDING_TEXT =
  'Desades al PMS, però Beds24 no ha confirmat el canvi: revisa-les a Beds24 (no es reintenta sol)';

const isString = (value: unknown): value is string => typeof value === 'string' && value !== '';

/** Un número de reserva i una data, amb el mateix patró que el backend (`write-action.ts`). */
const SAFE_RESERVATION_REF = /^[A-Za-z0-9][A-Za-z0-9_-]{0,31}$/;
const SAFE_YMD = /^\d{4}-\d{2}-\d{2}$/;
const MAX_PARTIAL_REFS = 100;

const safeRef = (value: unknown): value is string => typeof value === 'string' && SAFE_RESERVATION_REF.test(value);
const safeYmd = (value: unknown): string | undefined =>
  typeof value === 'string' && SAFE_YMD.test(value) ? value : undefined;

/**
 * Una proposta aplicada a mitges, com la pinta la targeta. `beds24Pending` (al contracte de
 * `ProposalPartial`): reserves desades al PMS que Beds24 no ha confirmat; ni fetes, ni fallades, ni per fer.
 */
export type CardPartial = ProposalPartial;

/** Una llista de `{ reservation, checkIn?, checkOut? }`: el que no casa amb el patró, fora. */
function refsWithDates(list: unknown): ProposalPartialPending[] {
  const out: ProposalPartialPending[] = [];
  for (const item of (Array.isArray(list) ? list : []).slice(0, MAX_PARTIAL_REFS)) {
    if (!item || typeof item !== 'object') continue;
    const raw = item as { reservation?: unknown; checkIn?: unknown; checkOut?: unknown };
    if (!safeRef(raw.reservation)) continue;
    const checkIn = safeYmd(raw.checkIn);
    const checkOut = safeYmd(raw.checkOut);
    out.push({ reservation: raw.reservation, ...(checkIn ? { checkIn } : {}), ...(checkOut ? { checkOut } : {}) });
  }
  return out;
}

/**
 * El `partial` del servidor, llegit amb prudència i validat amb patró (números de reserva i dates
 * `AAAA-MM-DD`, com fa el backend): el que no s'entén no es pinta.
 */
export function readPartial(value: unknown): CardPartial | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as { done?: unknown; failed?: unknown; pending?: unknown; beds24Pending?: unknown };
  const done = (Array.isArray(raw.done) ? raw.done : []).filter(safeRef).slice(0, MAX_PARTIAL_REFS);
  const failed = safeRef(raw.failed) ? raw.failed : null;
  const pending = refsWithDates(raw.pending);
  const beds24Pending = refsWithDates(raw.beds24Pending);
  if (done.length === 0 && !failed && pending.length === 0 && beds24Pending.length === 0) return undefined;
  return { done, failed, pending, ...(beds24Pending.length > 0 ? { beds24Pending } : {}) };
}

/**
 * Només hi ha reserves que Beds24 no ha confirmat: res no ha fallat ni queda per fer. La targeta
 * no diu «ha fallat» sinó `BEDS24_PENDING_STATUS_TEXT`.
 */
export function isOnlyBeds24Pending(partial: CardPartial | undefined): boolean {
  return !!partial?.beds24Pending?.length && !partial.failed && partial.pending.length === 0;
}

/** Una resposta que diu que s'ha escrit alguna cosa, encara que no tot (`partial` amb contingut). */
export function wroteSomething(resolution: ProposalResolution | undefined): boolean {
  if (!resolution) return false;
  if (resolution.status === 'approved' || resolution.status === 'undone') return true;
  return resolution.status === 'failed' && !!readPartial(resolution.partial);
}

/**
 * El que la targeta diu a més de l'estat: l'error, l'avís d'una escriptura feta (`attention`) i el
 * detall d'una aplicada a mitges (`partial`). Mana la resposta del clic d'ara i, si no n'hi ha, la
 * consulta d'estat: en reobrir la conversa s'ha de veure el mateix que just després de clicar.
 *
 * `outputAttention` és l'avís que ja portava la sortida de la tool (una acció executada).
 */
export function resolutionNotes(
  resolved: ProposalResolution | undefined,
  fetched: ProposalResolution | undefined,
  outputAttention?: string,
): { error?: string; attention?: string; partial?: CardPartial } {
  const current = currentResolution(resolved, fetched);
  const failed = current?.status === 'failed';
  const partial = failed ? readPartial(current?.partial) : undefined;
  // Una acció desfeta ja no té res per mirar; un «Desfés» fallit la deixa com era, amb el seu avís.
  const attention =
    current?.status === 'undone'
      ? undefined
      : [resolved?.attention, fetched?.attention, outputAttention].find(isString);
  const message = failed ? current?.error : undefined;
  return {
    ...(isString(message) ? { error: message } : {}),
    ...(attention && !partial ? { attention } : {}),
    ...(partial ? { partial } : {}),
  };
}

/** L'avís d'una escriptura que s'ha fet però deixa alguna cosa per mirar. Ben visible. */
export function ProposalAttention({ text }: { text: string }) {
  return (
    <p
      className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 font-medium text-amber-900 text-xs dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
      role="alert"
      data-testid="proposal-attention"
    >
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
      <span>{text}</span>
    </p>
  );
}

/** Una llista de reserves amb les dates on havien de quedar, si en porten. */
function ReservationRefList({ items }: { items: ProposalPartialPending[] }) {
  return (
    <ul className="list-disc pl-4">
      {items.map((item) => (
        <li key={item.reservation} className="break-words">
          {item.reservation}
          {item.checkIn && item.checkOut ? ` → del ${item.checkIn} al ${item.checkOut}` : ''}
        </li>
      ))}
    </ul>
  );
}

/**
 * Què s'ha fet i què queda d'una proposta aplicada a mitges. Les reserves que Beds24 no ha
 * confirmat (`beds24Pending`) són un grup propi: ja són desades al PMS i no es tornen a demanar
 * (el PMS ja té el valor nou i no s'escriuria res), així que no hi ha cap botó; es revisen a Beds24.
 */
export function ProposalPartialList({ partial }: { partial: CardPartial }) {
  const beds24Pending = partial.beds24Pending ?? [];
  const onlyBeds24 = isOnlyBeds24Pending(partial);
  return (
    <div
      className="space-y-1 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-amber-900 text-xs dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
      role="alert"
      data-testid="proposal-partial"
    >
      <p className="flex items-center gap-1.5 font-medium">
        <AlertTriangle className="size-3.5 shrink-0" />
        {onlyBeds24 ? 'Què s\'ha desat i què falta a Beds24' : 'Què s\'ha fet i què queda per fer'}
      </p>
      {/* Només Beds24 per confirmar i cap altra feta: no cal dir «Fetes: cap». */}
      {(!onlyBeds24 || partial.done.length > 0) && (
        <p>
          <span className="font-medium">Fetes:</span> {partial.done.length > 0 ? partial.done.join(', ') : 'cap'}
        </p>
      )}
      {beds24Pending.length > 0 && (
        <div data-testid="proposal-partial-beds24">
          <p className="font-medium">{BEDS24_PENDING_TEXT}:</p>
          <ReservationRefList items={beds24Pending} />
        </div>
      )}
      {partial.failed && (
        <p>
          <span className="font-medium">Ha fallat:</span> {partial.failed} (mira-la abans de tornar-hi)
        </p>
      )}
      {partial.pending.length > 0 && (
        <div>
          <p className="font-medium">Queden per fer:</p>
          <ReservationRefList items={partial.pending} />
        </div>
      )}
    </div>
  );
}

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '(buit)';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return '(valor no llegible)';
  }
};

/**
 * Com es diu cada camp de `changes` a la targeta. Els de crear una reserva (`estada`, `titular`,
 * `preu`, `ocupacio`, `nota`…) i els d'editar-ne una (`phone`, `notes`, `checkIn`…).
 */
const FIELD_LABELS: Record<string, string> = {
  estada: 'Estada',
  titular: 'Titular',
  preu: 'Preu',
  ocupacio: 'Ocupació',
  nota: 'Nota interna',
  viatgers: 'Viatgers',
  client: 'Client de facturació',
  phone: 'Telèfon',
  guestPhone: 'Telèfon',
  email: 'Correu',
  guestEmail: 'Correu',
  name: 'Nom',
  guestName: 'Nom',
  notes: 'Notes internes',
  arrivalTime: 'Hora d\'arribada',
  adults: 'Adults',
  children: 'Nens',
  checkIn: 'Entrada',
  checkOut: 'Sortida',
  price: 'Preu',
  totalPrice: 'Preu',
  room: 'Habitació',
};

/**
 * L'etiqueta d'un camp. Un camp que la targeta no coneix també es pinta, amb el seu nom fet
 * llegible (`hora_sortida` → «Hora sortida», `lateCheckout` → «Late checkout»): un camp nou del
 * backend no s'ha de perdre perquè el frontend encara no el sap dir.
 */
export function fieldLabel(field: string): string {
  if (Object.prototype.hasOwnProperty.call(FIELD_LABELS, field)) return FIELD_LABELS[field];
  const words = field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Camp';
}

const isEmptyBefore = (value: unknown) => value === null || value === undefined;

/**
 * L'abans i el després d'una proposta, un canvi per fila, amb l'etiqueta del camp en català.
 *
 * - Una creació (cap canvi no té valor d'abans): només el valor nou, sense «(buit) →».
 * - L'etiqueta del que es toca (`target.label`) surt un cop per grup de files seguides iguals.
 * - Ample de mòbil: la llista és un contenidor (`@container`) i, si fa menys de 24rem, l'abans i
 *   el després s'apilen en vertical (la fletxa apunta avall); amb més lloc, van en una línia.
 */
export function ProposalChanges({ changes }: { changes: ProposalChange[] }) {
  if (changes.length === 0) return null;
  const creation = changes.every((change) => isEmptyBefore(change.before));
  return (
    <ul className="@container min-w-0 space-y-1.5 text-sm" data-testid="proposal-changes">
      {changes.map((change, index) => {
        const label = change.target?.label;
        const showLabel = !!label && label !== changes[index - 1]?.target?.label;
        return (
          <li
            key={index}
            className="min-w-0 rounded-md border bg-background/60 px-2.5 py-1.5"
            data-field={change.field}
          >
            {showLabel && <p className="min-w-0 break-words font-medium text-xs">{label}</p>}
            <p className="min-w-0 break-words text-muted-foreground text-xs" title={change.field}>
              {fieldLabel(change.field)}
            </p>
            {creation ? (
              <p className="mt-0.5 min-w-0 font-medium [overflow-wrap:anywhere]">{formatValue(change.after)}</p>
            ) : (
              <div
                className="mt-0.5 flex min-w-0 flex-col items-start gap-0.5 @sm:flex-row @sm:flex-wrap @sm:items-center @sm:gap-x-2"
                data-testid="proposal-change-values"
              >
                <span className="min-w-0 max-w-full text-muted-foreground line-through [overflow-wrap:anywhere]">
                  {formatValue(change.before)}
                </span>
                <ArrowRight
                  className="size-3.5 shrink-0 rotate-90 text-muted-foreground @sm:rotate-0"
                  aria-label="passa a"
                />
                <span className="min-w-0 max-w-full font-medium [overflow-wrap:anywhere]">{formatValue(change.after)}</span>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** El número de Beds24 d'una creació, si és dels que «Obre la fitxa» sap obrir (8 xifres). */
const BEDS24_ID = /^\d{8}$/;

/**
 * Els textos que el backend escriu en crear (`create-stays.plan.ts`): el número hi surt sempre,
 * encara que el sobre que arriba al navegador només porti `success` i `message`.
 */
const CREATED_MESSAGE_PATTERNS: readonly RegExp[] = [
  /^Reserva creada: (\d{8})\b/,
  /S'HA CREAT \(reserva (\d{8})\b/,
  /^Grup creat:[^.]*\bmestra (\d{8})\b/,
  /^Habitació afegida al grup (\d{8})\b/,
];

/**
 * La reserva que una proposta de crear ha creat, pel `result` de l'aprovació:
 *
 * - `new_reservation` i `add_room_to_group`: `result.reservation.beds24Id` (l'habitació nova), o
 *   `result.beds24Id` a l'arrel.
 * - `new_group`: `result.masterBeds24Id` (la mestra) o, si no hi és, el primer de `beds24Ids`.
 * - Els camps manen; només si el sobre no en porta cap (un resultat desat abans que el backend
 *   els deixés passar), el número que diu el `message` de la creació.
 *
 * Només un número de Beds24 de 8 xifres: amb qualsevol altre, cap enllaç.
 */
export function createdReservationBeds24Id(result: unknown): string | undefined {
  if (!result || typeof result !== 'object') return undefined;
  const raw = result as {
    reservation?: unknown;
    beds24Id?: unknown;
    masterBeds24Id?: unknown;
    beds24Ids?: unknown;
    message?: unknown;
  };
  const reservation =
    raw.reservation && typeof raw.reservation === 'object'
      ? (raw.reservation as { beds24Id?: unknown }).beds24Id
      : undefined;
  const firstOfGroup = Array.isArray(raw.beds24Ids) ? raw.beds24Ids[0] : undefined;
  for (const candidate of [reservation, raw.beds24Id, raw.masterBeds24Id, firstOfGroup]) {
    if (typeof candidate === 'string' && BEDS24_ID.test(candidate)) return candidate;
  }
  if (typeof raw.message === 'string') {
    for (const pattern of CREATED_MESSAGE_PATTERNS) {
      const found = raw.message.match(pattern)?.[1];
      if (found) return found;
    }
  }
  return undefined;
}

/** El text curt dels botons desactivats mentre Miranda encara escriu el missatge. */
export const STREAMING_HINT = 'Espera que Miranda acabi';

/** Una proposta que el servidor encara aplica (un clic ha respost `pending`). */
export const APPLYING_TEXT = 'S\'està aplicant… La targeta s\'actualitzarà sola.';

/** Botons d'acció de les targetes: 40 px d'alt, que es puguin tocar amb el dit al mòbil. */
export const CARD_ACTION_CLASS = 'h-10 px-3 text-sm';

export interface ProposalCardProps {
  /** La sortida (ja filtrada) d'una tool d'escriptura. */
  result: WriteToolResult;
  actions?: ProposalActions;
  /**
   * Demanar l'estat actual a `GET /agent/proposals/:id` en muntar: en reobrir una conversa, la
   * sortida desada diu `pending` encara que ja s'hagi resolt, i una acció executada torna a
   * oferir «Desfés» encara que ja sigui desfeta. Es demana per a una proposta (`proposalId`) i
   * per a una acció executada amb `undoId`. Si l'endpoint no respon, val el que diu la sortida.
   */
  fetchStatus?: boolean;
  /** El `DecisionController` de la targeta. Els tests hi posen el seu, com `actions`. */
  controller?: DecisionController<ProposalAction, ProposalResolution>;
  /**
   * Miranda encara escriu el missatge d'aquesta targeta. El xat fa scroll al final a cada tros i
   * la targeta es mou sota el dit: «Aprova», «Rebutja» i «Desfés» queden desactivats, amb
   * `STREAMING_HINT` al costat, fins que acaba.
   */
  isStreaming?: boolean;
  className?: string;
}

/**
 * Targeta d'una escriptura de Miranda, segons el `kind` del contracte:
 *
 * - `proposal`: no s'ha escrit res. L'abans i el després, «Aprova» i «Rebutja». Una creació
 *   (reserva, grup, habitació d'un grup) aprovada ofereix «Obre la fitxa» de la reserva nova
 *   (`createdReservationBeds24Id`).
 * - `executed`: ja s'ha escrit. Els canvis i, si hi ha `undoId` i el servidor no diu que ja és
 *   desfeta, en curs o fallida (`executedView`), «Desfés».
 * - `refused`: Miranda no ho fa mai sola. Només s'explica.
 *
 * «El primer guanya»: la resposta de qualsevol acció porta l'estat ja resolt, encara que l'hagi
 * resolt una altra persona; la targeta pinta el que diu el servidor, no el que s'ha clicat.
 */
export function ProposalCard({
  result,
  actions = DEFAULT_ACTIONS,
  fetchStatus = true,
  controller: givenController,
  isStreaming = false,
  className,
}: ProposalCardProps) {
  const queryClient = useQueryClient();
  const hintId = useId();
  const { controller, state } = useDecision<ProposalAction, ProposalResolution>(givenController);
  const changes = Array.isArray(result.changes) ? result.changes : [];
  const proposalId = result.kind === 'proposal' ? result.proposalId : undefined;
  const statusId = proposalStatusId(result);

  const working = state.phase === 'working';
  const resolved = state.phase === 'done' ? state.result : undefined;
  // Un clic que ha respost `pending` (el servidor encara aplicava) també demana l'estat, encara
  // que la targeta no l'hagués de demanar en muntar.
  const { data: fetched, isLoading: statusLoading } = useQuery({
    ...proposalStatusQuery(statusId, fetchStatus || resolved?.status === 'pending'),
    // Mentre el servidor aplica o desfà, es torna a demanar fins que acaba (no hi ha botons morts).
    refetchInterval: (query) => statusPollInterval(result, resolved, query.state.data),
  });

  const run = (action: ProposalAction, id: string | undefined) => {
    decideProposal({ controller, actions, queryClient, action, id, proposalId: statusId });
  };

  // L'error, l'avís i el «en part»: de la resposta del clic o, en reobrir, de la consulta d'estat.
  const notes = resolutionNotes(resolved, fetched, result.kind === 'executed' ? result.attention : undefined);
  const error = state.phase === 'error' ? state.error : notes.error;

  // ── refused ──────────────────────────────────────────────────────────────────────────────
  if (result.kind === 'refused') {
    return (
      <div
        className={cn('flex min-w-0 max-w-full gap-2 rounded-md border border-dashed bg-muted/40 p-3 text-sm', className)}
        data-testid="proposal-card"
        data-kind="refused"
      >
        <Ban className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 space-y-1 break-words">
          <p className="font-medium">Miranda no ho fa sola</p>
          {result.summary && <p className="text-muted-foreground">{result.summary}</p>}
        </div>
      </div>
    );
  }

  // ── executed ─────────────────────────────────────────────────────────────────────────────
  if (result.kind === 'executed') {
    const view = executedView(resolved, fetched);
    const undone = view.state === 'undone';
    const executedError = error ?? view.error;
    // Una execució que ha fallat a mitges també diu què s'ha fet; si només falta Beds24, no «ha fallat».
    const partial = view.state === 'failed' ? notes.partial : undefined;
    const beds24Only = isOnlyBeds24Pending(partial);
    return (
      <div
        className={cn('min-w-0 max-w-full space-y-2 rounded-md border bg-background p-3 text-sm', className)}
        data-testid="proposal-card"
        data-kind="executed"
        data-status={view.state}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="gap-1 text-xs">
            {undone && <Undo2 className="size-3" />}
            {view.state === 'in-progress' && <Loader2 className="size-3 animate-spin" />}
            {view.state === 'failed' && <X className="size-3 text-destructive" />}
            {view.state === 'done' && <Check className="size-3 text-green-600" />}
            {view.state === 'failed' && beds24Only ? BEDS24_PENDING_STATUS_TEXT : EXECUTED_BADGE[view.state]}
          </Badge>
          {result.summary && <span className={cn('min-w-0 break-words font-medium', undone && 'line-through')}>{result.summary}</span>}
        </div>
        <ProposalChanges changes={changes} />
        {notes.attention && !undone && <ProposalAttention text={notes.attention} />}
        {partial && <ProposalPartialList partial={partial} />}
        {view.state === 'in-progress' && (
          <p className="text-muted-foreground text-xs" data-testid="proposal-in-progress">
            S'està aplicant o desfent ara mateix. La targeta s'actualitzarà sola.
          </p>
        )}
        {executedError && <p className="text-destructive text-xs" role="alert">{executedError}</p>}
        {result.undoId && view.state === 'done' && !resolved && (
          // Una sola línia de 40 px: l'avís de «espera» hi cap tallat i no canvia l'alçada.
          <div className="flex min-w-0 items-center justify-end gap-2">
            {isStreaming && (
              <span id={hintId} className="min-w-0 truncate text-muted-foreground text-xs" data-testid="streaming-hint">
                {STREAMING_HINT}
              </span>
            )}
            <ConfirmationAction
              variant="outline"
              className={cn(CARD_ACTION_CLASS, 'shrink-0')}
              // Mentre el servidor no ha dit com està, no es pot desfer: potser ja ho és.
              disabled={working || isStreaming || (fetchStatus && statusLoading)}
              aria-describedby={isStreaming ? hintId : undefined}
              onClick={() => run('undo', result.undoId)}
            >
              {working ? <Loader2 className="size-3.5 animate-spin" /> : <Undo2 className="size-3.5" />}
              Desfés
            </ConfirmationAction>
          </div>
        )}
      </div>
    );
  }

  // ── proposal ─────────────────────────────────────────────────────────────────────────────
  // L'estat que mana: el de l'acció d'ara, si no el que diu el servidor, si no el de la sortida.
  // Un clic que ha respost `pending` és «s'està aplicant» fins que la consulta diu com ha acabat.
  const { status, applying } = proposalView(resolved, fetched, result.status);
  const pending = status === 'pending' && !applying;
  const approvalId = proposalId ?? 'proposal';
  // Una creació aplicada: «Obre la fitxa» de la reserva nova (la de l'aprovació d'ara o, en
  // reobrir la conversa, la que diu el servidor).
  const createdId =
    status === 'approved' ? createdReservationBeds24Id(currentResolution(resolved, fetched)?.result) : undefined;
  const decideDisabled = working || !proposalId || isStreaming;

  return (
    <Confirmation
      className={cn('min-w-0 max-w-full bg-background px-3', className)}
      data-testid="proposal-card"
      data-kind="proposal"
      data-status={applying ? 'applying' : status}
      // La parella que entén `Confirmation`: demanada mentre és pendent, resposta quan no. Mentre
      // s'aplica, resposta sense veredicte: ni botons, ni «aprovada», ni «rebutjada».
      state={pending ? 'approval-requested' : 'approval-responded'}
      approval={
        pending || applying ? { id: approvalId } : { id: approvalId, approved: status === 'approved' }
      }
    >
      <ConfirmationTitle className="block space-y-2">
        <span className="block break-words font-medium">{result.summary || 'Proposta de canvi'}</span>
        <ProposalChanges changes={changes} />
      </ConfirmationTitle>

      <ConfirmationRequest>
        {/* Mentre Miranda escriu, la mateixa línia diu per què els botons no van: cap fila de més. */}
        <p id={hintId} className="text-muted-foreground text-xs" data-testid={isStreaming ? 'streaming-hint' : undefined}>
          Encara no s'ha escrit res. {isStreaming ? STREAMING_HINT : STATUS_TEXT.pending}.
        </p>
      </ConfirmationRequest>
      {applying && (
        <p
          className="inline-flex items-center gap-1.5 text-muted-foreground text-xs"
          role="status"
          data-testid="proposal-applying"
        >
          <Loader2 className="size-3.5 shrink-0 animate-spin" />
          {APPLYING_TEXT}
        </p>
      )}
      <ConfirmationAccepted>
        <p className="inline-flex items-center gap-1.5 text-green-700 text-xs dark:text-green-500">
          <Check className="size-3.5" />
          {STATUS_TEXT.approved}
        </p>
      </ConfirmationAccepted>
      <ConfirmationRejected>
        <p className="inline-flex items-center gap-1.5 text-muted-foreground text-xs">
          <X className="size-3.5" />
          {status === 'failed' && notes.partial
            ? isOnlyBeds24Pending(notes.partial)
              ? BEDS24_PENDING_STATUS_TEXT
              : PARTIAL_STATUS_TEXT
            : STATUS_TEXT[status === 'approved' || status === 'pending' ? 'rejected' : status]}
        </p>
      </ConfirmationRejected>

      {status === 'approved' && notes.attention && <ProposalAttention text={notes.attention} />}
      {createdId && (
        <div className="flex min-w-0 flex-wrap" data-testid="proposal-open-sheet">
          <ReservationSheetLink beds24Id={createdId} />
        </div>
      )}
      {status === 'failed' && notes.partial && <ProposalPartialList partial={notes.partial} />}
      {error &&<p className="text-destructive text-xs" role="alert">{error}</p>}
      {!proposalId && pending && (
        <p className="text-destructive text-xs" role="alert">
          La proposta no porta identificador: no es pot aprovar des d'aquí.
        </p>
      )}

      <ConfirmationActions className="flex-wrap">
        <ConfirmationAction
          variant="outline"
          className={CARD_ACTION_CLASS}
          disabled={decideDisabled}
          aria-describedby={isStreaming ? hintId : undefined}
          onClick={() => run('reject', proposalId)}
        >
          Rebutja
        </ConfirmationAction>
        <ConfirmationAction
          className={CARD_ACTION_CLASS}
          disabled={decideDisabled}
          aria-describedby={isStreaming ? hintId : undefined}
          onClick={() => run('approve', proposalId)}
        >
          {working && <Loader2 className="size-3.5 animate-spin" />}
          Aprova
        </ConfirmationAction>
      </ConfirmationActions>
    </Confirmation>
  );
}

function isProposalStatus(value: unknown): value is ProposalStatus {
  return typeof value === 'string' && value in STATUS_TEXT;
}
